import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Title } from 'react-admin';
import Graph from 'graphology';
import circular from 'graphology-layout/circular';
import FA2 from 'graphology-layout-forceatlas2';
import Sigma from 'sigma';
import { adminGet } from '../config/api';

const GRAPH_LIMIT = 5000;

const COLORS = {
    subject: '#1976d2',
    object: '#ffb300',
    predicate: '#00897b',
    user: '#455a64',
};

const NODE_BASE_SIZES = {
    subject: 3.4,
    object: 3.3,
    predicate: 2.6,
    user: 2.8,
    default: 3,
};

const EDGE_STYLES = {
    WIK: { color: '#2e7d32', size: 1.35 },
    WIT: { color: '#7b1fa2', size: 1 },
    BOTH: { color: '#1565c0', size: 1.2 },
    default: { color: '#999', size: 0.8 },
};

const EDGE_LAYER_RANK = {
    WIK: 0,
    default: 1,
    BOTH: 2,
    WIT: 3,
};

const WITWIK_LABELS = {
    WIK: 'Je sais que',
    WIT: 'Je pense que',
    BOTH: 'Je sais et je pense que',
};

const NODE_KIND_LABELS = {
    subject: 'Subject',
    object: 'Oobject',
    predicate: 'Prédicat',
    user: 'Utilisateur',
};

const STATUS_OPTIONS = [
    { value: '', label: 'Tous les RDF' },
    { value: 'VALIDATED', label: 'Validés' },
    { value: 'PENDING', label: 'En attente' },
    { value: 'REJECTED', label: 'Rejetés' },
];

const normalize = (value) => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function getEdgeStyle(witWik) {
    return EDGE_STYLES[witWik] || EDGE_STYLES.default;
}

function getDensityScale(order) {
    if (order > 1800) return 0.62;
    if (order > 1000) return 0.72;
    if (order > 500) return 0.82;
    return 1;
}

function getNodeBaseSize(kind) {
    return NODE_BASE_SIZES[kind] || NODE_BASE_SIZES.default;
}

function normalizeGraphVisualStyle(g) {
    const densityScale = getDensityScale(g.order);

    g.forEachNode((nodeId, attrs = {}) => {
        const degree = typeof g.degree === 'function' ? g.degree(nodeId) : 0;
        const degreeBoost = Math.min(1.4, Math.log1p(degree) * 0.32);
        const size = Math.max(1.8, Math.min(4.8, (getNodeBaseSize(attrs.kind) + degreeBoost) * densityScale));
        g.setNodeAttribute(nodeId, 'size', size);
    });

    g.forEachEdge((edgeId, attrs = {}) => {
        const edgeStyle = getEdgeStyle(attrs.witWik);
        g.setEdgeAttribute(edgeId, 'size', edgeStyle.size);
    });
}

function applyGraphLabelMode(g, layoutName) {
    const forceStructuredNodeLabels = layoutName === 'structured';
    const forceStructuredEdgeLabels = forceStructuredNodeLabels && g.size <= 700;

    g.forEachNode((nodeId) => {
        g.setNodeAttribute(nodeId, 'forceLabel', forceStructuredNodeLabels);
    });

    g.forEachEdge((edgeId) => {
        g.setEdgeAttribute(edgeId, 'forceLabel', forceStructuredEdgeLabels);
    });
}

function compareEdgesForLayering(leftEdge, rightEdge) {
    const leftRank = EDGE_LAYER_RANK[leftEdge?.witWik] ?? EDGE_LAYER_RANK.default;
    const rightRank = EDGE_LAYER_RANK[rightEdge?.witWik] ?? EDGE_LAYER_RANK.default;
    return leftRank - rightRank;
}

function formatEdgeTooltip(attrs) {
    const stance = WITWIK_LABELS[attrs?.witWik] || null;
    const rawLabel = attrs?.label || null;
    const normalizedLabel = rawLabel?.toLowerCase();
    const labelRepeatsStance = stance && ['sait que', 'pense que', 'sait & pense que'].includes(normalizedLabel);
    return [stance || rawLabel, labelRepeatsStance ? null : rawLabel, attrs?.createdBy ? `par ${attrs.createdBy}` : null]
        .filter(Boolean)
        .join('\n');
}

function mergeGraph(g, payload) {
    if (!payload) return;
    const { nodes = [], edges = [] } = payload;

    nodes.forEach((node) => {
        if (!g.hasNode(node.id)) {
            g.addNode(node.id, {
                label: node.label || node.shortLabel || node.uuid || node.id,
                kind: node.kind,
                uuid: node.uuid || null,
                shortLabel: node.shortLabel || null,
                color: COLORS[node.kind] || '#888',
                size: getNodeBaseSize(node.kind),
                x: Math.random(),
                y: Math.random(),
            });
        }
    });

    [...edges].sort(compareEdgesForLayering).forEach((edge) => {
        if (!g.hasEdge(edge.id) && g.hasNode(edge.source) && g.hasNode(edge.target)) {
            const edgeStyle = getEdgeStyle(edge.witWik);
            g.addEdgeWithKey(edge.id, edge.source, edge.target, {
                label: edge.label || '',
                color: edgeStyle.color,
                size: edgeStyle.size,
                predicateId: edge.predicateId || null,
                rdfId: edge.rdfId || null,
                witWik: edge.witWik || null,
                createdAt: edge.createdAt || null,
                createdBy: edge.createdBy || null,
            });
        }
    });

    normalizeGraphVisualStyle(g);
}

function sortedNodesByLabel(g, nodes) {
    return [...nodes].sort((left, right) => {
        const leftLabel = g.getNodeAttribute(left, 'label') || left;
        const rightLabel = g.getNodeAttribute(right, 'label') || right;
        return leftLabel.localeCompare(rightLabel);
    });
}

function assignStructuredGlobalLayout(g) {
    const byKind = {
        user: sortedNodesByLabel(g, g.filterNodes((node, attrs) => attrs.kind === 'user')),
        subject: sortedNodesByLabel(g, g.filterNodes((node, attrs) => attrs.kind === 'subject')),
        predicate: sortedNodesByLabel(g, g.filterNodes((node, attrs) => attrs.kind === 'predicate')),
        object: sortedNodesByLabel(g, g.filterNodes((node, attrs) => attrs.kind === 'object')),
    };
    const columns = [
        ['user', -6],
        ['subject', -2],
        ['predicate', 2],
        ['object', 6],
    ];

    columns.forEach(([kind, x]) => {
        const nodes = byKind[kind] || [];
        const gap = Math.max(0.55, Math.min(1.4, 24 / Math.max(nodes.length, 1)));
        const firstY = -((nodes.length - 1) * gap) / 2;
        nodes.forEach((nodeId, index) => {
            g.setNodeAttribute(nodeId, 'x', x);
            g.setNodeAttribute(nodeId, 'y', firstY + index * gap);
        });
    });
}

function applyGraphLayout(g, layoutName) {
    try {
        if (layoutName === 'structured') {
            assignStructuredGlobalLayout(g);
        } else if (layoutName === 'fa2') {
            FA2.assign(g, { iterations: 600, settings: { gravity: 0.1, scalingRatio: 10 } });
        } else {
            circular.assign(g);
        }
    } catch {
        circular.assign(g);
    }
}

function getRelatedEdges(graph, nodeId, limit = 12) {
    const relatedEdges = [];
    if (!graph || !nodeId) return relatedEdges;

    try {
        graph.forEachEdge((edgeId, attrs = {}, source, target, sourceAttrs = {}, targetAttrs = {}) => {
            if (source !== nodeId && target !== nodeId) return;
            const outgoing = source === nodeId;
            const otherAttrs = outgoing ? targetAttrs : sourceAttrs;
            relatedEdges.push({
                id: edgeId,
                direction: outgoing ? 'sortant' : 'entrant',
                label: attrs.label || 'Relation',
                stance: WITWIK_LABELS[attrs.witWik] || attrs.witWik || null,
                color: getEdgeStyle(attrs.witWik).color,
                otherLabel: otherAttrs?.label || otherAttrs?.shortLabel || (outgoing ? target : source),
                createdBy: attrs.createdBy || null,
                rdfId: attrs.rdfId || null,
            });
        });
    } catch {
        return relatedEdges;
    }

    return relatedEdges.slice(0, limit);
}

function getNodeDetails(graph, nodeId) {
    if (!graph?.hasNode(nodeId)) return null;
    const attrs = graph.getNodeAttributes(nodeId);
    const uuid = attrs.uuid || nodeId?.slice(2) || '';
    const relatedEdges = getRelatedEdges(graph, nodeId);
    return {
        id: nodeId,
        kind: attrs.kind || 'unknown',
        kindLabel: NODE_KIND_LABELS[attrs.kind] || 'Entité',
        uuid,
        label: attrs.label || attrs.shortLabel || uuid || nodeId,
        shortLabel: attrs.shortLabel || null,
        color: attrs.color || '#888',
        degree: typeof graph.degree === 'function' ? graph.degree(nodeId) : relatedEdges.length,
        relatedEdges,
    };
}

function waitForSizedContainer(el, attempts = 20) {
    return new Promise((resolve) => {
        let count = 0;
        const check = () => {
            const { clientWidth, clientHeight } = el || {};
            if (clientWidth > 0 && clientHeight > 0) return resolve(true);
            count += 1;
            if (count >= attempts) return resolve(false);
            setTimeout(check, 50);
        };
        check();
    });
}

export default function RdfGraphPage() {
    const containerRef = React.useRef(null);
    const graphRef = React.useRef(null);
    const sigmaRef = React.useRef(null);
    const tooltipRef = React.useRef(null);
    const resizeObsRef = React.useRef(null);
    const dragCleanupRef = React.useRef(null);

    const [status, setStatus] = React.useState('');
    const [layout, setLayout] = React.useState('structured');
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [selectedNode, setSelectedNode] = React.useState(null);
    const [query, setQuery] = React.useState('');
    const [stats, setStats] = React.useState({ rdf: 0, nodes: 0, edges: 0, last: true });
    const isDesktop = useMediaQuery('(min-width:900px)');

    const hasSize = React.useCallback((el) => Boolean(el && el.clientWidth > 0 && el.clientHeight > 0), []);

    const safeRefresh = React.useCallback((renderer) => {
        if (!renderer || !hasSize(containerRef.current)) return;
        try {
            renderer.refresh();
        } catch {}
    }, [hasSize]);

    const hardPurgeContainer = React.useCallback(() => {
        if (sigmaRef.current) {
            try {
                sigmaRef.current.kill();
            } catch {}
            sigmaRef.current = null;
        }
        if (tooltipRef.current?.parentNode) {
            tooltipRef.current.parentNode.removeChild(tooltipRef.current);
            tooltipRef.current = null;
        }
        if (dragCleanupRef.current) {
            try {
                dragCleanupRef.current();
            } catch {}
            dragCleanupRef.current = null;
        }
        if (resizeObsRef.current) {
            try {
                resizeObsRef.current.disconnect();
            } catch {}
            resizeObsRef.current = null;
        }
        if (containerRef.current) {
            while (containerRef.current.firstChild) {
                containerRef.current.removeChild(containerRef.current.firstChild);
            }
            containerRef.current.style.background = '#f7f7f7';
        }
    }, []);

    const loadGraph = React.useCallback(async () => {
        setError(null);
        setLoading(true);
        setSelectedNode(null);
        hardPurgeContainer();

        try {
            const params = new URLSearchParams({ limit: String(GRAPH_LIMIT) });
            if (status) params.set('status', status);
            const payload = await adminGet(`/rdf/graph?${params.toString()}`);

            const graph = new Graph({ type: 'directed', multi: true });
            mergeGraph(graph, payload);
            applyGraphLayout(graph, layout);
            applyGraphLabelMode(graph, layout);
            graphRef.current = graph;

            const rdfIds = new Set();
            (payload?.edges || []).forEach((edge) => {
                if (edge.rdfId) rdfIds.add(edge.rdfId);
            });
            setStats({
                rdf: rdfIds.size,
                nodes: graph.order,
                edges: graph.size,
                last: payload?.last !== false,
            });

            await waitForSizedContainer(containerRef.current);
            if (!containerRef.current) return;

            const renderer = new Sigma(graph, containerRef.current, {
                renderLabels: true,
                labelColor: { color: '#000' },
                renderEdgeLabels: true,
                edgeLabelColor: { color: '#000' },
                edgeLabelSize: 9,
                edgeLabelFont: 'sans-serif',
                edgeLabelWeight: '400',
                defaultEdgeType: 'arrow',
                enableEdgeHoverEvents: true,
                zIndex: true,
                hideLabelsOnMove: true,
                labelDensity: 1,
                labelGridCellSize: 48,
                labelRenderedSizeThreshold: 1,
                itemSizesReference: 'screen',
                zoomToSizeRatioFunction: (ratio) => Math.min(1.8, Math.max(0.75, 1 / Math.sqrt(ratio || 1))),
                minCameraRatio: 0.02,
                maxCameraRatio: 10,
                allowInvalidContainer: true,
            });
            sigmaRef.current = renderer;
            safeRefresh(renderer);

            dragCleanupRef.current = enableNodeDrag(renderer, graph);

            const tooltip = document.createElement('div');
            tooltip.style.position = 'absolute';
            tooltip.style.pointerEvents = 'none';
            tooltip.style.padding = '4px 6px';
            tooltip.style.background = 'rgba(255,255,255,0.95)';
            tooltip.style.border = '1px solid #ddd';
            tooltip.style.borderRadius = '6px';
            tooltip.style.fontSize = '12px';
            tooltip.style.color = '#000';
            tooltip.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
            tooltip.style.whiteSpace = 'pre-line';
            tooltip.style.display = 'none';
            containerRef.current.appendChild(tooltip);
            tooltipRef.current = tooltip;

            renderer.on('enterEdge', (event) => {
                const attrs = graph.getEdgeAttributes(event.edge);
                const tooltipText = formatEdgeTooltip(attrs);
                if (!tooltipText) return;
                tooltip.textContent = tooltipText;
                tooltip.style.left = `${event.event.x + 10}px`;
                tooltip.style.top = `${event.event.y + 10}px`;
                tooltip.style.display = 'block';
            });
            renderer.on('leaveEdge', () => {
                tooltip.style.display = 'none';
            });
            renderer.on('clickStage', () => {
                tooltip.style.display = 'none';
            });
            renderer.on('clickNode', ({ node }) => {
                setSelectedNode(getNodeDetails(graph, node));
            });

            if (window.ResizeObserver) {
                const resizeObserver = new ResizeObserver(() => safeRefresh(renderer));
                resizeObserver.observe(containerRef.current);
                resizeObsRef.current = resizeObserver;
            } else {
                const onResize = () => safeRefresh(renderer);
                window.addEventListener('resize', onResize, { passive: true });
                resizeObsRef.current = { disconnect: () => window.removeEventListener('resize', onResize) };
            }
        } catch (loadError) {
            setError(loadError?.message || 'Impossible de charger le graphe RDF.');
        } finally {
            setLoading(false);
        }
    }, [hardPurgeContainer, layout, safeRefresh, status]);

    React.useEffect(() => {
        loadGraph();
        return () => hardPurgeContainer();
    }, [hardPurgeContainer, loadGraph]);

    const applyLayout = React.useCallback((layoutName) => {
        setLayout(layoutName);
        const graph = graphRef.current;
        const renderer = sigmaRef.current;
        if (!graph || !renderer) return;
        applyGraphLayout(graph, layoutName);
        applyGraphLabelMode(graph, layoutName);
        safeRefresh(renderer);
    }, [safeRefresh]);

    const focusSearch = React.useCallback(() => {
        const graph = graphRef.current;
        const renderer = sigmaRef.current;
        const camera = renderer?.getCamera();
        const term = normalize(query);
        if (!graph || !renderer || !camera || !term) return;

        const match = graph.findNode((nodeId, attrs) => {
            const haystack = normalize(`${attrs.label || ''} ${attrs.shortLabel || ''} ${attrs.uuid || ''} ${nodeId}`);
            return haystack.includes(term);
        });
        if (!match) return;

        setSelectedNode(getNodeDetails(graph, match));
        const position = renderer.getNodeDisplayData(match);
        if (position) camera.animate(position, { duration: 450 });
    }, [query]);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
            <Title title="Graphe RDF global" />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                        Graphe RDF global
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Le même explorateur visuel que le front public, appliqué à l’ensemble des RDF administrables.
                    </Typography>
                </Box>
                <TextField
                    size="small"
                    label="Rechercher un nœud"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') focusSearch();
                    }}
                    sx={{ minWidth: 240 }}
                />
                <Button variant="outlined" startIcon={<SearchIcon />} onClick={focusSearch}>
                    Focus
                </Button>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="rdf-graph-status-label">Statut</InputLabel>
                    <Select
                        labelId="rdf-graph-status-label"
                        label="Statut"
                        value={status}
                        onChange={(event) => setStatus(event.target.value)}
                    >
                        {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value || 'all'} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadGraph} disabled={loading}>
                    Actualiser
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!stats.last && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Le graphe affiche les {GRAPH_LIMIT} RDF les plus récents.
                </Alert>
            )}

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip label={`${stats.rdf} RDF`} />
                <Chip label={`${stats.nodes} nœuds`} />
                <Chip label={`${stats.edges} liens`} />
                <LegendItem color={EDGE_STYLES.WIK.color} label="Je sais que" />
                <LegendItem color={EDGE_STYLES.WIT.color} label="Je pense que" />
            </Stack>

            <Box sx={{ mb: 1 }}>
                <Button size="small" variant={layout === 'structured' ? 'contained' : 'outlined'} onClick={() => applyLayout('structured')} sx={{ mr: 1 }}>
                    Structuré
                </Button>
                <Button size="small" variant={layout === 'fa2' ? 'contained' : 'outlined'} onClick={() => applyLayout('fa2')} sx={{ mr: 1 }}>
                    ForceAtlas2
                </Button>
                <Button size="small" variant={layout === 'circular' ? 'contained' : 'outlined'} onClick={() => applyLayout('circular')}>
                    Circulaire
                </Button>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        Chargement…
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                <Card sx={{ flex: 1, minWidth: 320 }}>
                    <CardContent sx={{ p: 0 }}>
                        <Box
                            ref={containerRef}
                            sx={{
                                position: 'relative',
                                height: 'calc(100vh - 330px)',
                                minHeight: 620,
                                borderRadius: 2,
                                overflow: 'hidden',
                                background: '#f7f7f7',
                            }}
                        />
                    </CardContent>
                </Card>

                {isDesktop && selectedNode && (
                    <GraphEntityPanel node={selectedNode} onClose={() => setSelectedNode(null)} />
                )}
            </Box>

            <GraphEntityDialog node={!isDesktop ? selectedNode : null} onClose={() => setSelectedNode(null)} />
        </Box>
    );
}

function LegendItem({ color, label }) {
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color }} />
            <Typography variant="caption" sx={{ color: '#111', fontWeight: 700 }}>
                {label}
            </Typography>
        </Box>
    );
}

function GraphEntityDialog({ node, onClose }) {
    return (
        <Dialog open={Boolean(node)} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{node?.label || 'Entité du graphe'}</DialogTitle>
            <DialogContent dividers>
                <GraphEntityDetails node={node} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
}

function GraphEntityPanel({ node, onClose }) {
    return (
        <Card sx={{ width: 360, flex: '0 0 360px', height: 'calc(100vh - 330px)', minHeight: 620, overflow: 'auto' }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {node?.label || 'Entité du graphe'}
                    </Typography>
                    <Button size="small" onClick={onClose}>
                        Fermer
                    </Button>
                </Box>
                <GraphEntityDetails node={node} />
            </CardContent>
        </Card>
    );
}

function GraphEntityDetails({ node }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Chip
                label={node?.kindLabel || 'Entité'}
                size="small"
                sx={{ alignSelf: 'flex-start', bgcolor: node?.color || '#888', color: '#fff', fontWeight: 700 }}
            />
            {node?.shortLabel && (
                <Typography variant="body2">
                    <strong>Short:</strong> {node.shortLabel}
                </Typography>
            )}
            {node?.uuid && (
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    <strong>UUID:</strong> {node.uuid}
                </Typography>
            )}
            <Typography variant="body2">
                <strong>Liaisons chargées:</strong> {node?.degree ?? 0}
            </Typography>

            <Divider />

            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                RDF liés dans le graphe
            </Typography>
            {node?.relatedEdges?.length ? (
                node.relatedEdges.map((edge) => (
                    <Box key={edge.id} sx={{ border: '1px solid #eee', borderRadius: 1.5, p: 1 }}>
                        <Typography variant="caption" sx={{ color: edge.color, fontWeight: 800 }}>
                            {edge.stance || 'Relation'} · {edge.direction}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {edge.label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>
                            avec {edge.otherLabel}
                        </Typography>
                        {edge.createdBy && (
                            <Typography variant="caption" sx={{ display: 'block', color: '#777' }}>
                                par {edge.createdBy}
                            </Typography>
                        )}
                        {edge.rdfId && (
                            <Typography variant="caption" sx={{ display: 'block', color: '#777', wordBreak: 'break-all' }}>
                                RDF {edge.rdfId}
                            </Typography>
                        )}
                    </Box>
                ))
            ) : (
                <Typography variant="body2" sx={{ color: '#666' }}>
                    Aucune liaison chargée pour cette entité.
                </Typography>
            )}
        </Box>
    );
}

function enableNodeDrag(renderer, graph, { preventClickAfterDrag = true } = {}) {
    const captor = renderer.getMouseCaptor();
    const camera = renderer.getCamera();

    let draggedNode = null;
    let isDragging = false;
    let hasMoved = false;
    let savedCamera = null;

    const onDownNode = (event) => {
        draggedNode = event.node;
        isDragging = true;
        hasMoved = false;
        savedCamera = { ...camera.getState() };
        event.event?.preventSigmaDefault?.();
        event.event?.originalEvent?.preventDefault?.();
        event.event?.originalEvent?.stopPropagation?.();
    };

    const onMouseMove = (event) => {
        if (!isDragging || !draggedNode) return;
        const position = renderer.viewportToGraph({ x: event.x, y: event.y });
        graph.setNodeAttribute(draggedNode, 'x', position.x);
        graph.setNodeAttribute(draggedNode, 'y', position.y);
        if (savedCamera) camera.setState(savedCamera);
        hasMoved = true;
    };

    const onMouseUp = () => {
        isDragging = false;
        draggedNode = null;
        savedCamera = null;
    };

    const onClickNode = (event) => {
        if (preventClickAfterDrag && hasMoved) {
            hasMoved = false;
            event.event?.originalEvent?.preventDefault?.();
            event.event?.originalEvent?.stopPropagation?.();
        }
    };

    renderer.on('downNode', onDownNode);
    captor.on('mousemove', onMouseMove);
    captor.on('mouseup', onMouseUp);
    renderer.on('clickNode', onClickNode);

    return () => {
        try {
            renderer.removeListener('downNode', onDownNode);
            captor.removeListener('mousemove', onMouseMove);
            captor.removeListener('mouseup', onMouseUp);
            renderer.removeListener('clickNode', onClickNode);
        } catch {}
    };
}
